import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import { CONTACT } from '@/mocks/contactInfo';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { breadcrumb } from '@/lib/seo';
import { ARTICLES } from '@/mocks/articlesData';

interface ArticleCard {
  title: string;
  excerpt: string;
  link: string;
  imageUrl: string;
  imageAlt: string;
}

// Articles historiques (pages dédiées, hors système dynamique)
const ORIGIN: Record<string, ArticleCard> = {
  'rat-dans-la-maison-que-faire': {
    title: 'Rat dans la maison : que faire',
    excerpt: 'Vous entendez des bruits dans le galetas ? Identifier une infestation de rats et les solutions pour protéger votre maison en Guadeloupe.',
    link: '/blogue/rat-dans-la-maison-que-faire',
    imageUrl: '/images/blogue-rat-dans-la-maison-que-faire-1.webp',
    imageAlt: 'Galetas en bois sous toiture en tôle avec signes d\'infestation de rats en Guadeloupe',
  },
  'leptospirose-guadeloupe': {
    title: 'La leptospirose en Guadeloupe',
    excerpt: 'La maladie transmise par l\'urine des rats : pourquoi la Guadeloupe est très touchée, comment elle se transmet et comment s\'en protéger.',
    link: '/blogue/leptospirose-guadeloupe',
    imageUrl: '/images/blogue-leptospirose-guadeloupe-1.webp',
    imageAlt: 'Infographie médicale sur la prévention de la leptospirose en milieu tropical en Guadeloupe',
  },
  'se-debarrasser-des-ravets': {
    title: 'Comment se débarrasser des ravets',
    excerpt: 'Les ravets envahissent votre cuisine ? Les méthodes qui marchent, celles qui aggravent le problème, et quand arrêter de bricoler.',
    link: '/blogue/se-debarrasser-des-ravets',
    imageUrl: '/images/blogue-se-debarrasser-des-ravets-1.webp',
    imageAlt: 'Cuisine créole propre et lumineuse avec traitement anti-ravets en Guadeloupe',
  },
  'morsure-scolopendre-que-faire': {
    title: 'Morsure de scolopendre : que faire',
    excerpt: 'Mordu par une scolopendre en Guadeloupe ? Les bons gestes immédiats, les signes qui imposent un médecin, et comment éviter la prochaine.',
    link: '/blogue/morsure-scolopendre-que-faire',
    imageUrl: '/images/blogue-morsure-scolopendre-que-faire-1.webp',
    imageAlt: 'Jardin tropical luxuriant avec végétation dense propice aux scolopendres en Guadeloupe',
  },
};

interface Cluster {
  heading: string;
  slugs: string[];
}

// 40 articles regroupés par thématique (cluster SEO)
const CLUSTERS: Cluster[] = [
  {
    heading: 'Rats, souris & dératisation',
    slugs: [
      'rat-dans-la-maison-que-faire',
      'leptospirose-guadeloupe',
      'comment-les-rats-entrent-dans-une-maison',
      'bruits-combles-nuit-rat-souris-chauve-souris',
      'rats-jardin-poulailler-guadeloupe',
      'crottes-de-rat-reconnaitre-danger',
      'se-debarrasser-des-souris',
      'piege-a-rat-lequel-choisir',
      'odeur-rat-mort-dans-un-mur',
    ],
  },
  {
    heading: 'Ravets & cafards',
    slugs: [
      'se-debarrasser-des-ravets',
      'ravet-volant-maison-que-faire',
      'd-ou-viennent-les-ravets',
      'blatte-germanique-ou-americaine',
      'ravets-appartement-immeuble',
      'ravets-restaurant-obligations',
    ],
  },
  {
    heading: 'Scolopendres',
    slugs: [
      'morsure-scolopendre-que-faire',
      'scolopendre-guadeloupe-dangereuse',
      'empecher-scolopendres-entrer-maison',
      'bete-a-mille-pattes-especes-guadeloupe',
    ],
  },
  {
    heading: 'Termites',
    slugs: [
      'reconnaitre-infestation-termites',
      'diagnostic-termites-vente-obligatoire',
      'termites-ou-fourmis-ailees',
      'proteger-construction-neuve-termites',
      'termites-souterrains-ou-bois-sec',
    ],
  },
  {
    heading: 'Punaises de lit',
    slugs: [
      'reconnaitre-punaises-de-lit',
      'punaises-de-lit-hotel-gite',
      'punaises-de-lit-5-erreurs',
    ],
  },
  {
    heading: 'Moustiques & démoustication',
    slugs: [
      'moustique-tigre-guadeloupe',
      'se-proteger-dengue-maison',
      'eliminer-gites-larvaires-jardin',
    ],
  },
  {
    heading: 'Fourmis, guêpes & autres nuisibles',
    slugs: [
      'invasion-fourmis-cuisine',
      'fourmis-folles-prises-electriques',
      'nid-de-guepes-danger',
      'chauve-souris-dans-les-combles',
      'depigeonnage-toiture-balcon',
    ],
  },
  {
    heading: 'Saisons, prix & obligations pro',
    slugs: [
      'prix-traitement-nuisibles-guadeloupe',
      'nuisibles-saison-cyclonique-guadeloupe',
      'nuisibles-careme-secheresse',
      'plan-lutte-nuisibles-haccp-restaurant',
      'certibiocide-professionnel-certifie',
    ],
  },
];

function cardFor(slug: string): ArticleCard | null {
  if (ORIGIN[slug]) return ORIGIN[slug];
  const meta = ARTICLES[slug];
  if (!meta) return null;
  return {
    title: meta.title,
    excerpt: meta.description,
    link: `/blogue/${slug}`,
    imageUrl: meta.hero,
    imageAlt: meta.h1,
  };
}

export default function Blog() {
  useDocumentHead({
    title: 'Conseils anti-nuisibles en Guadeloupe — Gwada Nuisibles',
    description: 'Guides pratiques : se débarrasser des ravets, rats, scolopendres, punaises, termites, moustiques. Conseils adaptés au climat et au bâti guadeloupéens.',
    jsonLd: breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Blogue', path: '/blogue' }]),
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{ label: 'Accueil', href: '/' }, { label: 'Blogue' }]} />

      {/* HERO */}
      <section className="bg-primary-500 py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h1 className="font-heading font-black text-3xl md:text-4xl text-white leading-tight mb-4">
            Conseils anti-nuisibles en Guadeloupe
          </h1>
          <p className="text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">
            40 guides pratiques pour reconnaître, prévenir et gérer les nuisibles de Guadeloupe — et savoir quand un traitement professionnel devient nécessaire.
          </p>
        </div>
      </section>

      {/* ARTICLES PAR CLUSTER */}
      <section className="bg-background-100 py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 space-y-14 md:space-y-20">
          {CLUSTERS.map((cluster) => (
            <div key={cluster.heading}>
              <h2 className="font-heading font-black text-2xl md:text-3xl text-primary-900 mb-8 pb-3 border-b-2 border-secondary-300">
                {cluster.heading}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {cluster.slugs.map((slug) => {
                  const card = cardFor(slug);
                  if (!card) return null;
                  return (
                    <RevealWrapper key={card.link}>
                      <Link
                        to={card.link}
                        className="group flex flex-col h-full bg-white rounded-xl overflow-hidden border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
                      >
                        <div className="relative w-full h-44 overflow-hidden">
                          <img
                            src={card.imageUrl}
                            alt={card.imageAlt}
                            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-primary-900/50 to-transparent"></div>
                        </div>
                        <div className="p-6 flex flex-col flex-1">
                          <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2 group-hover:text-secondary-500 transition-colors">
                            {card.title}
                          </h3>
                          <p className="text-foreground-600 text-sm leading-relaxed mb-4 flex-1">
                            {card.excerpt}
                          </p>
                          <span className="inline-flex items-center gap-2 text-secondary-500 font-semibold text-sm group-hover:gap-3 transition-all whitespace-nowrap">
                            Lire l&rsquo;article
                            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                          </span>
                        </div>
                      </Link>
                    </RevealWrapper>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
            Un sujet que vous voudriez voir traité ?
          </h2>
          <p className="text-foreground-600 text-base leading-relaxed max-w-xl mx-auto mb-8">
            Dites-le-nous sur WhatsApp : nos articles sont écrits à partir des vraies questions de nos clients en Guadeloupe.
          </p>
          <a
            href={CONTACT.whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap px-8 py-3.5 rounded-lg font-semibold text-base transition-all duration-200 bg-whatsapp-500 text-white hover:bg-whatsapp-600 cursor-pointer"
          >
            <i className="ri-whatsapp-line text-xl w-5 h-5 flex items-center justify-center"></i>
            Proposer un sujet
          </a>
        </div>
      </section>

      <ContactFormBlock source="blogue" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
